import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField, Button,
  IconButton, Tooltip, CircularProgress, Alert, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Autocomplete, InputAdornment, ToggleButton, ToggleButtonGroup,
  Divider, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Payments as CollectionsIcon, Search as SearchIcon,
  CheckCircle as FullIcon, IndeterminateCheckBox as PartialIcon,
  Cancel as NoneIcon, EventBusy as AbsentIcon,
  School as SchoolDayIcon, FreeBreakfast as NoSchoolIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon, Class as ClassIcon,
  Map as MapIcon, HourglassBottom as PendingIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import useViewAs from './useViewAs';
import swal from '../utils/swal';
import DistributionMapDialog       from './DistributionMapDialog';
import ClassDistributionMapDialog  from './ClassDistributionMapDialog';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const AUTOSAVE_MS = 500;
const ALL_CLASSES = '— All classes —';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Derive a status from an amount vs the daily rate. Only used when the
// user is typing into Received — an explicit Absent tap wins over this.
function deriveStatus(amountStr, dailyAmount) {
  if (amountStr === '' || amountStr == null) return 'none';
  const amt = Number(amountStr);
  if (!Number.isFinite(amt) || amt <= 0) return 'none';
  const daily = Number(dailyAmount) || 0;
  if (amt >= daily && daily > 0) return 'full';
  return 'partial';
}

const STATUS_META = {
  full:    { label: 'Full',    color: 'success', icon: <FullIcon    fontSize="small" /> },
  partial: { label: 'Partial', color: 'warning', icon: <PartialIcon fontSize="small" /> },
  none:    { label: 'No pay',  color: 'error',   icon: <NoneIcon    fontSize="small" /> },
  absent:  { label: 'Absent',  color: 'default', icon: <AbsentIcon  fontSize="small" /> },
};

export default function TeacherPortalCollections() {
  // An admin previewing a teacher's portal — see useViewAs.js.
  const { viewAsParams } = useViewAs();
  // Teachers mark this roster on their phones, so below `md` the
  // seven-column table is replaced by one card per student. The table
  // needs ~1100px of width to show without squashing; a phone has
  // about 390px, which meant pinch-zooming and sideways scrolling to
  // reach the amount box for every single learner.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [date] = useState(todayISO());
  const [assignments, setAssignments] = useState([]);  // rows from /me/daily-income-types
  // The term/school-week verdict that came back with those rows. The
  // server owns it; this screen only renders it.
  const [periodMeta, setPeriodMeta]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [pickedType, setPickedType]   = useState(null); // { income_type, session_id, ... }
  const [session, setSession]         = useState(null);
  const [roster, setRoster]           = useState([]);
  const [totals, setTotals]           = useState({ expected: 0, received: 0, marked: 0, total: 0 });
  const [rosterLoading, setRLoad]     = useState(false);
  const [classFilter, setClassFilter] = useState(ALL_CLASSES);
  const [search, setSearch]           = useState('');
  const [rowState, setRowState]       = useState({});   // { student_id: { amount, saving, savedAt, error } }
  const [mapRow, setMapRow]           = useState(null);  // roster row currently shown in the map dialog
  const [classMapOpen, setClassMapOpen] = useState(false);
  const timers = useRef({});

  // ── Bootstrap: what income types can this teacher collect for today?
  const loadAssignments = async () => {
    setLoading(true);
    try {
      const r = await API.get('/collections/me/daily-income-types', { params: viewAsParams });
      const rows = r.data?.rows || [];
      setAssignments(rows);
      setPeriodMeta(r.data || null);
      // Auto-select the sole assignment for a smoother path — the majority
      // of teacher-collectors only handle one income type.
      if (rows.length === 1 && !pickedType) {
        setPickedType(rows[0]);
      } else if (pickedType && !rows.some(x => x.income_type === pickedType.income_type)) {
        setPickedType(null); // assignment was revoked mid-session
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load your collection assignments'));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAssignments(); /* eslint-disable-next-line */ }, []);

  // ── When an income type is picked: fetch or auto-open today's session.
  useEffect(() => {
    if (!pickedType) { setSession(null); setRoster([]); return; }
    (async () => {
      setRLoad(true);
      try {
        const r = await API.get('/daily-collections/sessions/today', {
          params: { income_type: pickedType.income_type, date },
        });
        setSession(r.data?.session || null);
        if (r.data?.session) await loadRoster(r.data.session.id);
        else { setRoster([]); setTotals({ expected: 0, received: 0, marked: 0, total: 0 }); }
      } catch (e) {
        swal.error(swal.pickError(e, "Could not load today's session"));
      } finally { setRLoad(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedType]);

  const loadRoster = async (sessionId) => {
    try {
      const r = await API.get(`/daily-collections/sessions/${sessionId}`);
      const rows = r.data?.roster || [];
      setRoster(rows);
      setTotals(r.data?.totals || { expected: 0, received: 0, marked: 0, total: 0 });
      // Seed rowState from saved rows so refresh doesn't wipe in-progress typing.
      setRowState(prev => {
        const next = { ...prev };
        for (const row of rows) {
          if (next[row.student_id] == null) {
            next[row.student_id] = {
              amount: row.amount_paid != null ? String(Number(row.amount_paid)) : '',
              saving: false, savedAt: 0, error: '',
            };
          }
        }
        return next;
      });
    } catch (e) { swal.error(swal.pickError(e, 'Could not load roster')); }
  };

  // ── Session control ────────────────────────────────────────
  const openSchoolDay = async () => {
    try {
      const r = await API.post('/daily-collections/sessions/open', {
        income_type: pickedType.income_type, session_date: date, is_school_day: 1,
      });
      setSession(r.data?.session || null);
      if (r.data?.session) loadRoster(r.data.session.id);
    } catch (e) { swal.error(swal.pickError(e, 'Could not open session')); }
  };
  const openNoSchool = async () => {
    const ok = await swal.confirm({
      title: 'Mark today as a non-school day?',
      text: 'No charges or arrears will be recorded for any student for today.',
      tone: 'warning', confirmText: 'Yes, no school today',
    });
    if (!ok) return;
    try {
      const r = await API.post('/daily-collections/sessions/open', {
        income_type: pickedType.income_type, session_date: date, is_school_day: 0,
      });
      setSession(r.data?.session || null);
    } catch (e) { swal.error(swal.pickError(e, 'Could not record non-school day')); }
  };

  // ── Autosave per row ────────────────────────────────────────
  // Amount typing: debounce → derive status → save.
  const scheduleSave = (row, amountStr) => {
    setRowState(prev => ({
      ...prev,
      [row.student_id]: { ...(prev[row.student_id] || {}), amount: amountStr, error: '' },
    }));
    if (timers.current[row.student_id]) clearTimeout(timers.current[row.student_id]);
    timers.current[row.student_id] = setTimeout(() => {
      const status = deriveStatus(amountStr, row.daily_amount);
      saveRow(row, status, amountStr);
    }, AUTOSAVE_MS);
  };

  // Explicit status button (Full/None/Absent) — immediate save, no debounce.
  const setStatus = (row, status) => {
    const currentAmt = Number(rowState[row.student_id]?.amount) || 0;
    const daily      = Number(row.daily_amount) || 0;
    let amount = rowState[row.student_id]?.amount ?? '';
    if (status === 'absent' || status === 'none') amount = '0';
    // Clicking "Full" defaults the input to the daily amount, but doesn't
    // override any larger amount the collector may have already typed —
    // overpayment stays intact and becomes credit at approval time.
    if (status === 'full' && currentAmt < daily) amount = String(daily);
    // Cancel any pending debounced save first.
    if (timers.current[row.student_id]) clearTimeout(timers.current[row.student_id]);
    setRowState(prev => ({
      ...prev,
      [row.student_id]: { ...(prev[row.student_id] || {}), amount, error: '' },
    }));
    saveRow(row, status, amount);
  };

  const saveRow = async (row, status, amountStr) => {
    const daily = Number(row.daily_amount) || 0;
    let amountPaid = Number(amountStr) || 0;
    if (status === 'absent' || status === 'none') amountPaid = 0;
    // Full: whatever the collector entered goes through — the backend
    // treats any amount ≥ daily as full and stores the overflow as
    // credit that spreads across future days.
    if (status === 'full' && amountPaid < daily) amountPaid = daily;
    if (status === 'partial') {
      if (!(amountPaid > 0 && amountPaid < daily)) {
        setRowState(prev => ({
          ...prev,
          [row.student_id]: { ...(prev[row.student_id] || {}),
            error: 'Partial needs an amount less than the daily rate' },
        }));
        return;
      }
    }
    setRowState(prev => ({
      ...prev,
      [row.student_id]: { ...(prev[row.student_id] || {}), saving: true, error: '' },
    }));
    try {
      await API.post(`/daily-collections/sessions/${session.id}/attendance`, {
        student_id: row.student_id, status, amount_paid: amountPaid,
      });
      // Refresh only this row's summary — pull the whole roster is a
      // cheap-enough round-trip (few hundred rows max) and keeps totals honest.
      await loadRoster(session.id);
      setRowState(prev => ({
        ...prev,
        [row.student_id]: { ...(prev[row.student_id] || {}),
          amount: amountPaid ? String(amountPaid) : '',
          saving: false, savedAt: Date.now() },
      }));
    } catch (e) {
      setRowState(prev => ({
        ...prev,
        [row.student_id]: { ...(prev[row.student_id] || {}),
          saving: false, error: swal.pickError(e, 'Save failed') },
      }));
    }
  };

  // ── Derived views ──────────────────────────────────────────
  const classOptions = useMemo(() => {
    const set = new Set();
    for (const r of roster) if (r.class_name) set.add(r.class_name);
    return [ALL_CLASSES, ...[...set].sort()];
  }, [roster]);

  const filteredRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter(r => {
      if (classFilter !== ALL_CLASSES && r.class_name !== classFilter) return false;
      if (!q) return true;
      return (r.student_name || '').toLowerCase().includes(q)
          || (r.student_id   || '').toLowerCase().includes(q);
    });
  }, [roster, search, classFilter]);

  const sessionLocked = session && (session.status === 'closed' || !session.is_school_day);

  // ── Render ────────────────────────────────────────────────
  if (loading) {
    return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }
  // The day is shut — a day the school does not collect on, or a term
  // that has lapsed. Shown before the "nothing assigned" state because
  // the teacher may well have routes; there is simply no day to work
  // them on. The buttons below would be refused by the server anyway,
  // and a "Yes — open today" that cannot be honoured is worse than no
  // button at all.
  if (periodMeta?.period_open === false) {
    const offDay = periodMeta.period_block_kind === 'non_school_day';
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
        {offDay
          ? <NoSchoolIcon sx={{ fontSize: 52, color: 'warning.main', mb: 1.5 }} />
          : <CollectionsIcon sx={{ fontSize: 52, color: 'warning.main', mb: 1.5 }} />}
        <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
          {offDay
            ? `No collections on ${periodMeta.day_name ? `a ${periodMeta.day_name}` : 'this day'}`
            : 'Collections are closed'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
          {periodMeta.period_message
            || 'No day can be opened and nothing can be recorded right now.'}
        </Typography>
      </Card>
    );
  }
  if (assignments.length === 0) {
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
        <CollectionsIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
          No collections assigned to you
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ask your school admin to assign you a daily-collection route
          (Settings → Collector Assignments), then reload this page.
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ fontFamily: FONT_ACCT }}>
      {/* Header — pick income type + show session status */}
      <Card sx={{ mb: 1.5, borderRadius: 2 }}>
        <Box sx={{
          px: { xs: 1.4, md: 2.5 }, py: { xs: 1.2, md: 1.6 },
          display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, flexWrap: 'wrap',
        }}>
          <CollectionsIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>Today · {date}</Typography>
          {/* On a phone the picker takes the full row rather than
              forcing a 240px minimum that pushes the chips off-screen. */}
          <Box sx={{ minWidth: { xs: '100%', sm: 240 }, flex: { xs: '1 1 100%', sm: '0 1 320px' } }}>
            {assignments.length === 1 ? (
              <Chip color="primary" variant="filled"
                icon={<CollectionsIcon fontSize="small" />}
                label={pickedType?.income_type || ''} sx={{ fontWeight: 800 }} />
            ) : (
              <Autocomplete size="small" options={assignments}
                value={pickedType} disableClearable
                onChange={(_, v) => setPickedType(v)}
                getOptionLabel={(o) => o?.income_type || ''}
                isOptionEqualToValue={(a, b) => a.income_type === b.income_type}
                renderInput={(p) => <TextField {...p} label="Collection" required />} />
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          {session && (
            <>
              <Chip size="small"
                icon={session.is_school_day ? <SchoolDayIcon fontSize="small" /> : <NoSchoolIcon fontSize="small" />}
                color={
                  session.status === 'closed'    ? 'default' :
                  session.status === 'no_school' ? 'warning' : 'success'
                }
                label={
                  session.status === 'closed'    ? 'Closed' :
                  session.status === 'no_school' ? 'No school today' : 'Open'
                }
                sx={{ fontWeight: 700 }} />
              <Tooltip title="Reload roster">
                <IconButton size="small" onClick={() => loadRoster(session.id)}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Card>

      {/* Body: session prompt / roster */}
      {!pickedType ? (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          Pick a collection above to load today's roster.
        </Alert>
      ) : rosterLoading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : !session ? (
        <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
          <SchoolDayIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
            Is today a school day?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Confirm before you start marking students. On a non-school day,
            no charges or arrears are recorded.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            {/* Full-width on a phone: these are the first thing a
                teacher taps each morning. */}
            <Button variant="contained" color="success" size="large"
              startIcon={<SchoolDayIcon />} onClick={openSchoolDay}
              sx={{ fontWeight: 700, minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
              Yes — open today
            </Button>
            <Button variant="outlined" color="warning" size="large"
              startIcon={<NoSchoolIcon />} onClick={openNoSchool}
              sx={{ fontWeight: 700, minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
              No school today
            </Button>
          </Stack>
        </Card>
      ) : !session.is_school_day ? (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
          Today is marked as a <b>non-school day</b>. No entries are recorded.
        </Alert>
      ) : (
        <>
          {/* Toolbar over the roster */}
          <Card sx={{ mb: 1.5, borderRadius: 2 }}>
            <Box sx={{
              px: { xs: 1.4, md: 2.5 }, py: { xs: 1.2, md: 1.4 },
              display: 'flex', alignItems: 'center', gap: { xs: 0.8, md: 1.2 }, flexWrap: 'wrap',
            }}>
              <Chip size="small" color="primary" variant="outlined"
                label={`${totals.marked} of ${totals.total} marked`}
                sx={{ fontWeight: 700 }} />
              <Chip size="small" variant="outlined"
                label={`Expected ${fmtMoney(totals.expected)}`}
                sx={{ fontWeight: 700 }} />
              <Chip size="small" color="success" variant="outlined"
                label={`Received ${fmtMoney(totals.received)}`}
                sx={{ fontWeight: 700 }} />
              <Divider orientation="vertical" flexItem
                sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { xs: '1 1 100%', sm: '0 1 260px' } }}>
                <Autocomplete size="small" options={classOptions}
                  value={classFilter} disableClearable
                  onChange={(_, v) => setClassFilter(v || ALL_CLASSES)}
                  renderInput={(p) => (
                    <TextField {...p} label="Class"
                      InputProps={{ ...p.InputProps,
                        startAdornment: (
                          <>
                            <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                            {p.InputProps.startAdornment}
                          </>
                        ) }} />
                  )} />
              </Box>
              <Tooltip title={`Roll-up map for ${classFilter === ALL_CLASSES ? 'the whole roster' : classFilter}`}>
                <span>
                  <Button size="small" variant="outlined" color="primary"
                    startIcon={<MapIcon fontSize="small" />}
                    onClick={() => setClassMapOpen(true)}
                    disabled={filteredRoster.length === 0}
                    sx={{ fontWeight: 700, textTransform: 'none' }}>
                    Class map
                  </Button>
                </span>
              </Tooltip>
              <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
              <TextField size="small" placeholder="Search name or ID"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ) }}
                sx={{ width: { xs: '100%', md: 240 } }} />
            </Box>
          </Card>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ pt: 1, px: { xs: 1, md: 2 } }}>
              {/* ── Phone / small tablet: one card per learner ──────
                  Same data and the same handlers as the table below,
                  laid out vertically so nothing needs horizontal
                  scrolling and the amount box is thumb-sized. */}
              {isMobile ? (
                <Stack spacing={1}>
                  {filteredRoster.length === 0 && (
                    <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">
                        No students match{classFilter !== ALL_CLASSES ? ` in ${classFilter}` : ''}
                        {search ? ` "${search}"` : ''}.
                      </Typography>
                    </Box>
                  )}
                  {filteredRoster.map((r, i) => {
                    const st       = rowState[r.student_id] || {};
                    const savedSt  = r.status || null;
                    const draftSt  = deriveStatus(st.amount, r.daily_amount);
                    const effectiveStatus = savedSt || draftSt;
                    const daily    = Number(r.daily_amount) || 0;
                    const recentlySaved = st.savedAt && (Date.now() - st.savedAt) < 2500;
                    const tone =
                      effectiveStatus === 'absent'  ? 'warning' :
                      effectiveStatus === 'full'    ? 'success' :
                      effectiveStatus === 'partial' ? 'info'    : 'grey';
                    return (
                      <Card key={r.student_id} variant="outlined" sx={{
                        borderRadius: 2,
                        opacity: sessionLocked ? 0.7 : 1,
                        borderLeft: '4px solid',
                        borderLeftColor: tone === 'grey' ? 'grey.300' : `${tone}.main`,
                      }}>
                        <Box sx={{ p: 1.3 }}>
                          {/* Who */}
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Avatar sx={{
                              width: 30, height: 30, fontSize: 12, mt: 0.2,
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                              color: 'primary.main',
                            }}>
                              <PersonIcon fontSize="inherit" />
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.25 }}>
                                {i + 1}. {r.student_name || '—'}
                              </Typography>
                              <Stack direction="row" spacing={0.6} alignItems="center"
                                flexWrap="wrap" sx={{ mt: 0.3 }}>
                                <Typography variant="caption" color="text.secondary" sx={TNUM}>
                                  {r.student_id}
                                </Typography>
                                {r.class_name && (
                                  <Chip size="small" label={r.class_name} variant="outlined"
                                    sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                                )}
                                {r.approval_status === 'PENDING' && (
                                  <Chip size="small" color="warning" variant="outlined"
                                    label="Pending" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                                )}
                                {r.approval_status === 'APPROVED' && (
                                  <Chip size="small" color="success"
                                    label="Approved"
                                    sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#fff' }} />
                                )}
                                {r.approval_status === 'REJECTED' && (
                                  <Chip size="small" color="error"
                                    label="Rejected"
                                    sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#fff' }} />
                                )}
                              </Stack>
                            </Box>
                            <Tooltip title="Distribution map">
                              <IconButton size="small" color="primary"
                                onClick={() => setMapRow(r)}>
                                <MapIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          {/* Money — the field a teacher actually taps */}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.1 }}>
                            <Typography variant="caption" color="text.secondary"
                              sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              Daily {fmtMoney(daily)}
                            </Typography>
                            <TextField size="small" type="number"
                              value={st.amount ?? ''}
                              onChange={(e) => scheduleSave(r, e.target.value)}
                              disabled={sessionLocked || effectiveStatus === 'absent'}
                              placeholder={fmtMoney(daily)}
                              error={!!st.error}
                              label="Received"
                              // A numeric keypad rather than the full
                              // keyboard — this field only ever takes money.
                              inputProps={{ min: 0, step: '0.01', inputMode: 'decimal',
                                style: { textAlign: 'right', ...TNUM } }}
                              sx={{ flex: 1 }} />
                            <Box sx={{ width: 20, textAlign: 'center' }}>
                              {st.saving && <CircularProgress size={14} />}
                              {!st.saving && recentlySaved && (
                                <FullIcon fontSize="small" sx={{ color: 'success.main' }} />
                              )}
                            </Box>
                          </Stack>
                          {st.error && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.3 }}>
                              {st.error}
                            </Typography>
                          )}

                          {/* Status — full width so each button is easy to hit */}
                          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 1 }}>
                            <ToggleButtonGroup size="small" exclusive fullWidth
                              value={effectiveStatus}
                              disabled={sessionLocked}
                              onChange={(_, v) => v && setStatus(r, v)}
                              sx={{ flex: 1, '& .MuiToggleButton-root': {
                                px: 0.6, py: 0.6, fontSize: 10.5, fontWeight: 800,
                                whiteSpace: 'nowrap', textTransform: 'uppercase',
                                lineHeight: 1.15,
                              } }}>
                              {Object.entries(STATUS_META).map(([key, meta]) => (
                                <ToggleButton key={key} value={key}
                                  color={meta.color === 'default' ? undefined : meta.color}>
                                  {meta.icon}
                                  <Box component="span" sx={{ ml: 0.4 }}>{meta.label}</Box>
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                            {savedSt === 'absent' && !sessionLocked && (
                              <Tooltip title="Marked absent by mistake? Reverse to Didn't pay">
                                <IconButton size="small" color="warning"
                                  onClick={() => setStatus(r, 'none')}>
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
              <TableContainer component={Paper} variant="outlined"
                sx={{ borderRadius: 2, maxHeight: '72vh',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <Table size="small" stickyHeader sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
                    color: 'primary.dark',
                    fontSize: 11, fontWeight: 800,
                    textTransform: 'uppercase', letterSpacing: 0.6,
                    borderBottom: '2px solid',
                    borderBottomColor: (t) => alpha(t.palette.primary.main, 0.30),
                  },
                  // Zebra striping — subtle alternating rows so the eye can track across.
                  '& tbody .MuiTableRow-root:nth-of-type(even)': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.025),
                  },
                  '& tbody .MuiTableRow-root:hover': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                  },
                  '& tbody .MuiTableCell-root': {
                    borderBottomColor: 'grey.100',
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }}>S/N</TableCell>
                      <TableCell sx={{ minWidth: 240 }}>Student</TableCell>
                      <TableCell sx={{ width: 100 }}>Class</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>Daily</TableCell>
                      <TableCell align="right" sx={{ width: 160 }}>Received (GHS)</TableCell>
                      <TableCell sx={{ width: 340 }}>Status</TableCell>
                      <TableCell align="center" sx={{ width: 130 }}>Approval / Map</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRoster.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          No students match{classFilter !== ALL_CLASSES ? ` in ${classFilter}` : ''}
                          {search ? ` "${search}"` : ''}.
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredRoster.map((r, i) => {
                      const st       = rowState[r.student_id] || {};
                      const savedSt  = r.status || null;
                      const draftSt  = deriveStatus(st.amount, r.daily_amount);
                      const effectiveStatus = savedSt || draftSt;
                      const daily    = Number(r.daily_amount) || 0;
                      const recentlySaved = st.savedAt && (Date.now() - st.savedAt) < 2500;
                      return (
                        <TableRow key={r.student_id} sx={{
                          '& td': { fontSize: 12, py: 0.7 },
                          opacity: sessionLocked ? 0.7 : 1,
                        }}>
                          <TableCell sx={{ color: 'text.disabled', ...TNUM }}>{i + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{
                                width: 26, height: 26, fontSize: 11,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                color: 'primary.main',
                              }}>
                                <PersonIcon fontSize="inherit" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                                  {r.student_name || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={TNUM}>
                                  {r.student_id}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {r.class_name
                              ? <Chip size="small" label={r.class_name} variant="outlined"
                                  sx={{ height: 20, fontWeight: 700 }} />
                              : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ ...TNUM, fontWeight: 700 }}>
                            {fmtMoney(daily)}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                              <TextField size="small" type="number"
                                value={st.amount ?? ''}
                                onChange={(e) => scheduleSave(r, e.target.value)}
                                disabled={sessionLocked || effectiveStatus === 'absent'}
                                placeholder={fmtMoney(daily)}
                                error={!!st.error}
                                inputProps={{ min: 0, step: '0.01',
                                  style: { textAlign: 'right', ...TNUM, padding: 4 } }}
                                sx={{ width: 110 }} />
                              <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {st.saving && <CircularProgress size={14} />}
                                {!st.saving && recentlySaved && (
                                  <FullIcon fontSize="small" sx={{ color: 'success.main' }} />
                                )}
                              </Box>
                            </Stack>
                            {st.error && (
                              <Typography variant="caption" color="error"
                                sx={{ display: 'block', textAlign: 'right' }}>
                                {st.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <ToggleButtonGroup size="small" exclusive
                                value={effectiveStatus}
                                disabled={sessionLocked}
                                onChange={(_, v) => v && setStatus(r, v)}
                                sx={{ '& .MuiToggleButton-root': {
                                  px: 1, py: 0.3, fontSize: 11, fontWeight: 700,
                                  whiteSpace: 'nowrap', textTransform: 'uppercase',
                                  lineHeight: 1.2,
                                } }}>
                                {Object.entries(STATUS_META).map(([key, meta]) => (
                                  <ToggleButton key={key} value={key}
                                    color={meta.color === 'default' ? undefined : meta.color}>
                                    {meta.icon}
                                    <Box component="span" sx={{ ml: 0.5 }}>{meta.label}</Box>
                                  </ToggleButton>
                                ))}
                              </ToggleButtonGroup>
                              {/* Undo Absent — appears only when this row was
                                  actually saved as absent. Reverses to "Didn't
                                  pay" so arrears accrue as they should. */}
                              {savedSt === 'absent' && !sessionLocked && (
                                <Tooltip title="Marked absent by mistake? Reverse to Didn't pay">
                                  <IconButton size="small" color="warning"
                                    onClick={() => setStatus(r, 'none')}>
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              {/* Approval status — only relevant when a cash
                                  entry exists (partial/full). Absent/none rows
                                  have no collector_collection to approve. */}
                              {r.approval_status === 'PENDING' && (
                                <Chip size="small" color="warning" variant="outlined"
                                  icon={<PendingIcon sx={{ fontSize: 12 }} />}
                                  label="Pending"
                                  sx={{ fontWeight: 700, height: 20 }} />
                              )}
                              {r.approval_status === 'APPROVED' && (
                                <Chip size="small" color="success" variant="filled"
                                  icon={<FullIcon sx={{ fontSize: 12 }} />}
                                  label="Approved"
                                  sx={{ fontWeight: 700, height: 20, color: '#fff' }} />
                              )}
                              {r.approval_status === 'REJECTED' && (
                                <Chip size="small" color="error" variant="filled"
                                  icon={<NoneIcon sx={{ fontSize: 12 }} />}
                                  label="Rejected"
                                  sx={{ fontWeight: 700, height: 20, color: '#fff' }} />
                              )}
                              <Tooltip title="Distribution map — see the projection">
                                <IconButton size="small" color="primary"
                                  onClick={() => setMapRow(r)}>
                                  <MapIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                Auto-saves as you type. Absent students don't accrue arrears.
                Received &lt; Daily → partial (shortfall carries as arrears).
                Received &gt; Daily → full (extra becomes credit that spreads
                to future days — see the Map).
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      <DistributionMapDialog
        open={!!mapRow}
        row={mapRow}
        workingDays={periodMeta?.school_week?.collections}
        onClose={() => setMapRow(null)} />

      <ClassDistributionMapDialog
        open={classMapOpen}
        className={classFilter === ALL_CLASSES ? 'All classes' : classFilter}
        incomeType={pickedType?.income_type || ''}
        rows={filteredRoster}
        onClose={() => setClassMapOpen(false)} />
    </Box>
  );
}
